import { Box, Grid, IconButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { PaymentService } from "services/PaymentService";
import Utils from "shared/helpers/Utils";
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
} from "shared/components/AppComponents";
import { ArrowBack, ExpandMore } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { AllCountries } from "shared/helpers/Countries";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { PaymentFormSchema } from "shared/helpers/Schema";
import { ProfileActions } from "state/profile/Action";
import { useProgress } from "context/ProgressProvider";
import Toast from "shared/helpers/Toast";
import { useIsMobile } from "pages/landing";
import { LoanService } from "services/LoanService";

const elementStyles = {
  style: {
    base: {
      fontSize: "16px",
      color: "#0A0A0A",
      "::placeholder": {
        color: "#aab7c4",
      },
      lineHeight: "1.4375em",
    },
    invalid: {
      color: "#d32f2f",
    },
  },
};

const PaymentForm = ({
  costData,
  currentProfile,
  PAGE_ID,
  currentStep,
  dataEligible,
  setPaymentData,
}: any) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { navigateToSectionStepHandler } = useProgress();

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const isMobile = useIsMobile();
  const { handleUpdateUserLoan } = useProgress();

  const getPaymentStatus = () => {
    PaymentService.getPaymentStatus(currentProfile.loadId)
      .then((response) => {
        let data = response.data.data.payment;
        setPaymentData({ ...data, ...costData });
        dispatch(
          ProfileActions.setRndProfile({
            paymentDetails: data,
          }),
        );
        navigateToSectionStepHandler(PAGE_ID, currentStep + 1);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const handleUpdateLoan = () => {
    LoanService.getRNDLoanReview(currentProfile.loadId)
      .then((response) => {
        handleUpdateUserLoan(response);
      })
      .catch((error) => {});
  };

  const createPay = (billingDetails: any) => {
    PaymentService.createPay(billingDetails)
      .then((response: any) => {
        console.log(response);
        getPaymentStatus();
        Toast.showSuccessMessage("Payment success");
        handleUpdateLoan();
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
        setIsProcessingPayment(false);
      })
      .finally(() => {});
  };

  const handleSubmit = async (formValues: any) => {
    if (!stripe || !elements || !clientSecret) {
      console.log("Payment system not ready.");
      return;
    }

    setIsProcessingPayment(true);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);

      if (!cardNumberElement) {
        throw new Error("Card element not found");
      }

      const { error: paymentMethodError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardNumberElement,
          billing_details: {
            name: formValues.cardName,
            email: formValues.email,
            address: {
              line1: formValues.cardAddress,
              country: formValues.cardCountry,
            },
          },
        });

      if (paymentMethodError) {
        throw new Error(paymentMethodError.message || "Invalid card");
      }

      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethod.id,
        });

      if (confirmError) {
        throw new Error(confirmError.message || "Payment confirmation failed");
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        console.log(paymentIntent);
        await createPay({
          paymentId: paymentIntent.id,
          invoiceTo: formValues.email,
          address: formValues.cardAddress,
          country: formValues.cardCountry,
        });
      } else {
        throw new Error("Payment was not completed");
      }
    } catch (error: any) {
      console.error("Payment error:", error);

      Utils.handleErrorResponse({
        response: {
          status: 400,
          data: { msg: error.message || "Payment failed" },
        },
      });
    } finally {
    }
  };

  useEffect(() => {
    if (costData?.clientSecretId) {
      setClientSecret(costData.clientSecretId);
    }
  }, [costData]);

  return (
    <Box
      sx={{
        padding: { xs: "40px", md: "40px 0" },
        boxShadow: "-24px 0px 32px 0px #0000001A",
        paddingLeft: { xs: "", md: "48px" },
        flexGrow: 1,
        height: "inherit",
      }}
    >
      <Box
        mb={5}
        gap={10}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h3" sx={{ mb: 3 }}>
          Pay with card
        </Typography>
      </Box>

      <Formik
        enableReinitialize
        validationSchema={PaymentFormSchema}
        validateOnMount
        onSubmit={(formValues, formik) => {
          handleSubmit(formValues);
        }}
        initialValues={{
          email: currentProfile?.email || "",
          cardName: "",
          cardCountry: "AU",
          cardAddress: "",
        }}
      >
        {({ handleSubmit, errors, values, touched }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <Box>
              <AppFormikTextField
                placeholder="Email"
                label="Email"
                name="email"
                type="email"
                outerLabel
                className="ezifin-default-field-dialog"
                helperText={false}
              />

              {/* Card Number */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body1"
                  className="formik-text-field-container"
                  sx={{ mb: "8px" }}
                >
                  Card details
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #E5E5E5",
                    borderRadius: "6px",
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    padding: "12px",
                    backgroundColor: "#f3f3f5",
                    "&:focus-within": {
                      borderColor: "#1976d2",
                      boxShadow: "0 0 0 1px #1976d2",
                    },
                    display: "flex",
                    position: "relative",
                  }}
                >
                  <img
                    src="/images/card-provider.png"
                    height={isMobile ? 18 : 24}
                    style={{
                      position: "absolute",
                      right: "0",
                      marginRight: "2vw",
                    }}
                  />
                  <CardNumberElement options={elementStyles} />
                </Box>
              </Box>

              {/* CVC */}
              <Box
                sx={{
                  display: "flex",
                  gap: 0,
                  mb: 2,
                  position: "relative",
                  top: "-7.4px",
                }}
              >
                <Box
                  sx={{
                    border: "1px solid #E5E5E5",
                    borderRadius: "6px",
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    borderRightWidth: 0,
                    padding: "12px",
                    backgroundColor: "#f3f3f5",
                    flex: 1,
                    "&:focus-within": {
                      borderColor: "#1976d2",
                      boxShadow: "0 0 0 1px #1976d2",
                      borderRightWidth: "1px",
                    },
                  }}
                >
                  <CardExpiryElement options={elementStyles} />
                </Box>
                <Box
                  sx={{
                    border: "1px solid #E5E5E5",
                    borderRadius: "6px",
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomLeftRadius: 0,
                    padding: "12px",
                    backgroundColor: "#f3f3f5",
                    flex: 1,
                    "&:focus-within": {
                      borderColor: "#1976d2",
                      boxShadow: "0 0 0 1px #1976d2",
                    },
                  }}
                >
                  <CardCvcElement options={elementStyles} />
                </Box>
              </Box>

              <AppFormikTextField
                placeholder="Full name"
                label="Name on the card"
                name="cardName"
                outerLabel
                className="ezifin-default-field-dialog"
                helperText={false}
              />

              <Box sx={{ mb: 2 }}>
                <AppFormikSelect
                  placeholder="Select country"
                  label="Billing address"
                  name="cardCountry"
                  outerLabel
                  className="ezifin-default-field-card-first"
                  items={AllCountries}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "6px",
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  }}
                  valueKey="value"
                  titleKey="label"
                  iconComponent={ExpandMore}
                  labelSize={"16px"}
                  helperText={false}
                />
                <AppFormikTextField
                  placeholder="Address"
                  name="cardAddress"
                  outerLabel
                  className="ezifin-default-field-card-second"
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "6px",
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                    },
                    position: "relative",
                    top: "-10.8px",
                  }}
                  helperText={false}
                />
              </Box>

              {false && (
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#77797D",
                    textDecoration: "underline",
                    mb: 3,
                    cursor: "default",
                    position: "relative",
                    top: "-12px",
                  }}
                >
                  Enter manually
                </Typography>
              )}

              <AppPrimaryButton
                fullWidth
                title={`Pay $${costData?.totalAmount.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} AUD`}
                loadingText="Processing Payment..."
                isLoading={isProcessingPayment}
                disabled={!stripe || !clientSecret || isProcessingPayment}
                onClick={handleSubmit}
              />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const StripeReviewDetails = ({
  PAGE_ID,
  currentStep,
  dataEligible,
  setPaymentData,
  costData,
}: any) => {
  const { navigateToSectionStepHandler, currentProfile } = useProgress();
  const stripePublicKey = process.env.REACT_APP_PUBLIC_STRIPE_KEY;

  const stripePromise = loadStripe(stripePublicKey);

  const actionOnBack = () => {
    navigateToSectionStepHandler(PAGE_ID, currentStep - 1);
  };

  return (
    <>
      {costData && (
        <Grid container component="main" sx={{ minHeight: "100vh" }}>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ color: "#0A0A0A" }}>
            <Box
              top="0"
              sx={{ padding: "40px 0", paddingRight: { xs: "", md: "72px" } }}
            >
              <IconButton
                onClick={actionOnBack}
                disableRipple
                sx={{ paddingLeft: 0, mb: 5, color: "#0A0A0A" }}
              >
                <ArrowBack sx={{ paddingLeft: 0, color: "#0A0A0A" }} />
                <img src="/images/ezifin-logo.png" alt="Logo" width={48} />
              </IconButton>

              <Typography variant="h3" sx={{ mb: 3, color: "#474747" }}>
                Pay EZIFIN Financial Services
              </Typography>

              <Box display="flex" gap={2} alignItems="center" mb={10}>
                <Typography variant="h2" sx={{ mb: 1 }}>
                  $
                  {(
                    Math.floor(costData?.totalAmount * 100) / 100
                  ).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography variant="h3" fontWeight={400}>
                  AUD
                </Typography>
              </Box>

              <Box mb={10}>
                {/* Business loan */}
                <Box
                  sx={{
                    display: "flex",
                    borderBottom: "1px solid #E5E5E5",
                    paddingBottom: "12px",
                    paddingRight: "12px",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="h3" fontWeight={400} fontSize={"17px"}>
                      Conditionally Approved Amount
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={400}
                      color="#474747"
                      fontSize={"13px"}
                    >
                      {(dataEligible && dataEligible.rateOfInterest) || "-"}{" "}
                      p.a.
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontSize={"17px"}>
                    $
                    {parseFloat(costData?.approvedLoanAmount).toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}{" "}
                    AUD
                  </Typography>
                </Box>

                {/* Application Fee */}
                <Box
                  sx={{
                    display: "flex",
                    borderBottom: "1px solid #E5E5E5",
                    paddingBottom: "12px",
                    paddingRight: "12px",
                    maxWidth: "95%",
                    marginLeft: "5%",
                    marginTop: "24px",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h3" fontWeight={400} fontSize={"17px"}>
                    Application Fee
                  </Typography>
                  <Typography variant="h3" fontSize={"17px"}>
                    $
                    {parseFloat(costData?.applicationFee).toLocaleString(
                      "en-US",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}{" "}
                    AUD
                  </Typography>
                </Box>

                {/* GST */}
                <Box
                  sx={{
                    display: "flex",
                    borderBottom: "1px solid #E5E5E5",
                    paddingBottom: "12px",
                    paddingRight: "12px",
                    maxWidth: "95%",
                    marginLeft: "5%",
                    marginTop: "24px",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h3" fontWeight={400} fontSize={"17px"}>
                    GST
                  </Typography>
                  <Typography variant="h3" fontSize={"17px"}>
                    $
                    {parseFloat(costData?.GST).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    AUD
                  </Typography>
                </Box>

                {/* Total */}
                <Box
                  sx={{
                    display: "flex",
                    borderBottom: "1px solid #E5E5E5",
                    paddingBottom: "12px",
                    paddingRight: "12px",
                    maxWidth: "95%",
                    marginLeft: "5%",
                    marginTop: "24px",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h3" fontSize={"17px"}>
                    Total
                  </Typography>
                  <Typography variant="h3" fontSize={"17px"}>
                    $
                    {(
                      Math.floor(costData?.totalAmount * 100) / 100
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    AUD
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ marginTop: "72px" }}>
                <img src="/images/poweredstripe.svg" alt="Powered by Stripe" />
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: "flex" }}>
            <Elements stripe={stripePromise}>
              <PaymentForm
                costData={costData}
                currentProfile={currentProfile}
                PAGE_ID={PAGE_ID}
                currentStep={currentStep}
                dataEligible={dataEligible}
                setPaymentData={setPaymentData}
              />
            </Elements>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default StripeReviewDetails;
